import type { Plan } from '@/lib/api';

// Paddle Plan IDs (live)
export const PLAN_IDS = {
  PRO_MONTHLY: "pri_01kvt6yfd26h0np7he4bvn9768",
  PRO_YEARLY: "pri_01kvt70gwt04gpye7kqqjpc3ze",
} as const;

export interface PublishedPlan {
  /** Backend plan id. Live plans merge onto the published one by this. */
  id: string;
  name: string;
  /** `null` is a custom tier with no published price; the API sends -1 for it. */
  price: number | null;
  currency: string;
  interval: string;
  features: readonly string[];
  /** One-line positioning shown under the plan name; not served by the API. */
  blurb: string;
}

/**
 * The published plan catalog, mirroring `ListPlans()` in
 * `backend/internal/billing/plans.go` — same ids, same order.
 *
 * The pricing page renders these before `/api/plans` answers, so the
 * prerendered HTML a crawler receives is the real pricing page rather than a
 * skeleton; a build container cannot reach the API. Live plans take over once
 * they load, so this list is a starting point, not the source of truth: keep it
 * in step with the backend when a plan is added or repriced.
 */
export const PUBLISHED_PLANS: readonly PublishedPlan[] = [
  {
    id: 'free',
    name: 'Starter',
    price: 0,
    currency: 'USD',
    interval: 'month',
    features: [
      '3 Active Repositories',
      '10 Scans per month',
      '1 Concurrent Job',
      'Basic debt analysis',
      'Community support',
    ],
    blurb: 'Perfect for side projects and indie hackers.',
  },
  {
    id: 'pro',
    name: 'Pro',
    price: 29,
    currency: 'USD',
    interval: 'month',
    features: [
      '100 Active Repositories',
      'Unlimited Scans',
      '4 Concurrent Jobs',
      'Advanced metrics & reports',
      'Priority email support',
    ],
    blurb: 'For growing teams shipping daily.',
  },
  {
    id: 'pro_yearly',
    name: 'Pro Yearly',
    price: 300,
    currency: 'USD',
    interval: 'year',
    features: [
      '100 Active Repositories',
      'Unlimited Scans',
      '4 Concurrent Jobs',
      'Advanced metrics & reports',
      'Priority email support',
      'Save $48/year',
    ],
    blurb: 'Everything in Pro, billed annually.',
  },
  {
    id: 'enterprise',
    name: 'Enterprise',
    price: null,
    currency: 'USD',
    interval: 'month',
    features: [
      'Everything in Pro',
      'SSO / SAML integration',
      'Custom integrations',
      'Dedicated support',
    ],
    blurb: 'Custom solutions for large organizations.',
  },
];

/** The plan the pricing page emphasises. */
export const FEATURED_PLAN_ID = 'pro';

export type PlanId = keyof typeof PLAN_IDS;

export interface PricingTier {
  id: string;
  name: string;
  blurb: string;
  /** `null` is a custom tier with no published price. */
  price: number | null;
  interval: string;
  currency: string;
  features: readonly string[];
}

function publishedTier(plan: PublishedPlan): PricingTier {
  return { ...plan };
}

/** The API marks a custom tier with -1; this page marks it with null. */
function liveTier(plan: Plan, published?: PublishedPlan): PricingTier {
  return {
    id: plan.id,
    name: plan.name,
    // The blurb is presentation copy the API does not serve.
    blurb: published?.blurb ?? '',
    price: plan.price === -1 ? null : plan.price,
    interval: plan.interval,
    currency: plan.currency,
    features: plan.features,
  };
}

/**
 * The plans to render.
 *
 * Before `/api/plans` answers — and if it never does — this is the published
 * catalog. Once it answers, the API is authoritative for which plans exist,
 * their order and their prices, so every plan it returns is rendered even if
 * the catalog has no entry for it. Matching is by plan id, not display name, so
 * repricing or renaming a plan cannot drop it from the page.
 */
function isPlan(value: unknown): value is Plan {
  if (typeof value !== 'object' || value === null) return false;
  const plan = value as Record<string, unknown>;
  return (
    typeof plan.id === 'string' &&
    typeof plan.name === 'string' &&
    typeof plan.price === 'number' &&
    typeof plan.currency === 'string' &&
    typeof plan.interval === 'string' &&
    Array.isArray(plan.features) &&
    plan.features.every((feature) => typeof feature === 'string')
  );
}

/**
 * The plans in an `/api/plans` response, or none.
 *
 * The API is an untrusted boundary and its declared return type is optimistic:
 * `api.request` resolves a 200 response it cannot parse as JSON to `{}` rather
 * than rejecting, so a misconfigured API base URL hands this page an object
 * where it expects an array. Anything that is not a well-formed plan is
 * dropped, which leaves the published catalog on screen instead of throwing
 * during render.
 */
export function toPlans(value: unknown): Plan[] {
  return Array.isArray(value) ? value.filter(isPlan) : [];
}

export function pricingTiers(
  published: readonly PublishedPlan[],
  live: readonly Plan[],
): PricingTier[] {
  if (live.length === 0) return published.map(publishedTier);

  const publishedById = new Map(published.map((plan) => [plan.id, plan]));
  return live.map((plan) => liveTier(plan, publishedById.get(plan.id)));
}
